
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Loader2, Gift, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import Navbar from '@/components/Navbar';
import { supabase } from '@/integrations/supabase/client';
import { APP_CONFIG } from '@/config/app-config';
import { useTranslation } from 'react-i18next';
import EmailVerificationWaiting from '@/components/auth/EmailVerificationWaiting';
import useSocialAuthHandler from '@/hooks/useSocialAuthHandler';
import { UnifiedLoader } from '@/components/dashboard/shared/UnifiedLoader';
import { applyReferralCode, validateReferralCodeFormat } from '@/utils/referral-helpers';
import { useAutoApplyReferral } from '@/hooks/useAutoApplyReferral';

const loginFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

const registerFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  firstName: z.string().min(2, { message: 'First name must be at least 2 characters' }),
  lastName: z.string().min(2, { message: 'Last name must be at least 2 characters' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"], // path of error
});

type LoginFormValues = z.infer<typeof loginFormSchema>;
type RegisterFormValues = z.infer<typeof registerFormSchema>;

const PractitionerAuth: React.FC = () => {
  useSocialAuthHandler();
  const { signIn, signUp, user, session, activeRole, roleReady, getUserType, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('practitioner-auth');
  const { t: tSettings } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [originalReferralCode, setOriginalReferralCode] = useState<string | null>(null);
  const [referralRemoved, setReferralRemoved] = useState(false);

  // Auto-apply referral code from metadata when user logs in
  useAutoApplyReferral();

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
    },
  });

  // Check for referral code in URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode && validateReferralCodeFormat(refCode.toUpperCase())) {
      const code = refCode.toUpperCase();
      setReferralCode(code);
      setOriginalReferralCode(code);
      setActiveTab('register'); // Auto-switch to registration mode
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user || !session) return;

    if ((activeRole as any) === 'practitioner') {
      navigate('/practitioner-dashboard');
      return;
    }

    // Wait for practitioner profile creation after email confirmation
    const channel = supabase
      .channel('practitioner-profile-wait')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'practitioners', filter: `id=eq.${user.id}` },
        () => {
          navigate('/practitioner-dashboard');
        }
      )
      .subscribe();

    const timeout = setTimeout(() => {
      if ((activeRole as any) === 'practitioner') navigate('/practitioner-dashboard');
    }, 5000);

    return () => {
      clearTimeout(timeout);
      supabase.removeChannel(channel);
    };
  }, [user, session, activeRole, navigate]);

  const onLoginSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      console.log('Practitioner login attempt:', { email: values.email });
      
      // Explicitly specify that we're logging in as a practitioner
      const { error } = await signIn(values.email, values.password, 'practitioner');
      
      if (error) {
        throw new Error(error.message);
      }
      
      navigate('/practitioner-dashboard');
    } catch (error: any) {
      console.error('Practitioner login error:', error);
      const msg = String(error?.message || '');
      if (/invalid login credentials/i.test(msg)) {
        try {
          // Check if email exists and what user type it has
          const { data, error: fnError } = await supabase.functions.invoke('check-email-availability', {
            body: { email: values.email, userType: 'practitioner' }
          });
          
          let description = t('wrongPassword');
          if (!fnError && data && data.details) {
            if (data.details.emailExists === false) {
              description = '此邮箱尚未注册从业者账户。请先注册或检查您是否使用了客户账户登录。';
            } else if (data.details.has_client_profile && !data.details.has_practitioner_profile) {
              description = '此邮箱已注册为客户账户。请使用客户登录页面，或注册新的从业者账户。';
            } else if (data.details.can_register_as_practitioner === false) {
              description = '此邮箱无法作为从业者登录。请检查您的账户类型。';
            }
          }
          toast({ variant: 'destructive', title: '登录失败', description });
        } catch (e) {
          toast({ variant: 'destructive', title: 'Login Failed', description: t('wrongPassword') });
        }
      } else if (/Profile Creation Failed/i.test(msg)) {
        toast({ 
          variant: 'destructive', 
          title: '账户不存在', 
          description: '此邮箱没有从业者档案。请先注册从业者账户或使用正确的登录页面。' 
        });
      } else {
        toast({ variant: 'destructive', title: 'Login Failed', description: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const onRegisterSubmit = async (values: RegisterFormValues) => {
    setLoading(true);
    try {
      console.log('Practitioner registration attempt:', { 
        email: values.email, 
        firstName: values.firstName, 
        lastName: values.lastName 
      });
      
      // First check email availability
      const { checkEmailAvailability, getEmailConflictMessage } = await import('@/utils/email-availability');
      const availabilityResult = await checkEmailAvailability(values.email, 'practitioner');
      
      if (!availabilityResult.available) {
        const conflictMessage = getEmailConflictMessage(availabilityResult, 'practitioner', i18n.language.startsWith('zh') ? 'zh' : 'en');
        
        // Show simplified error messages without action buttons
        console.log('Email availability check failed:', availabilityResult);
        
        toast({
          variant: 'destructive',
          title: t('errors.emailAlreadyRegistered'),
          description: conflictMessage,
        });
        return;
      }
      
      // Detect user location and timezone during registration
      const { detectUserLocationAndTimezone } = await import('@/utils/location-timezone-detection');
      const locationData = await detectUserLocationAndTimezone();

      // Enhanced profile data with explicit practitioner type
      const profileData = {
        user_type: 'practitioner', // Explicitly set user type
        first_name: values.firstName,
        last_name: values.lastName,
        preferred_language: 'en',
        timezone: locationData.timezone,
        country: locationData.location.country || undefined,
        // Include referral code if present and not removed
        referral_code_to_apply: (originalReferralCode && !referralRemoved) ? originalReferralCode : undefined
      };

      console.log('Sending profile data:', profileData);

      const { error, data } = await signUp(values.email, values.password, 'practitioner', profileData);

      if (error) {
        console.error('Registration error:', error);
        throw new Error(error.message);
      }

      console.log('Registration successful for practitioner');
      // Show email verification waiting page instead of just toast
      setRegisteredEmail(values.email);
      setShowEmailVerification(true);
    } catch (error: any) {
      console.error('Practitioner registration failed:', error);
      toast({
        variant: 'destructive',
        title: 'Registration Failed',
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('Google login attempt for practitioner');
      sessionStorage.setItem('intended_role', 'practitioner');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/practitioner-auth`,
          queryParams: {
            role: 'practitioner', // Custom parameter to indicate intended role
            prompt: 'select_account' // Force account selection
          }
        }
      });
      if (error) throw error;
    } catch (error: any) {
      console.error('Google login failed:', error);
      toast({
        variant: 'destructive',
        title: 'Google Login Failed',
        description: error.message,
      });
    }
  };

  const signInWithLinkedIn = async () => {
    try {
      console.log('LinkedIn login attempt for practitioner');
      sessionStorage.setItem('intended_role', 'practitioner');
      // Pass the practitioner role to LinkedIn sign-in
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/practitioner-auth`,
          queryParams: {
            role: 'practitioner', // Custom parameter to indicate intended role
            prompt: 'select_account' // Force account selection
          }
        }
      });
      if (error) throw error;
    } catch (error: any) {
      console.error('LinkedIn login failed:', error);
      toast({
        variant: 'destructive',
        title: 'LinkedIn Login Failed',
        description: error.message,
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show email verification waiting page
  if (showEmailVerification) {
    return (
      <EmailVerificationWaiting
        email={registeredEmail}
        userType="practitioner"
        onBackToLogin={() => {
          setShowEmailVerification(false);
          setActiveTab('login');
          setRegisteredEmail('');
        }}
      />
    );
  }

  // Show loading only for authenticated users who need profile creation
  if (user && session && roleReady) {
    // If user has a different role (client/admin), redirect them
    if (activeRole && activeRole !== 'practitioner') {
      const targetPath = activeRole === 'client' ? '/client-dashboard' : 
                        activeRole === 'admin' ? '/admin/dashboard' : '/';
      window.location.href = targetPath;
      return <UnifiedLoader message="Redirecting..." fullScreen />;
    }
    
    // If user is authenticated but no role is set, they might be a new practitioner
    // Only show creating profile message for new users, not existing ones
    if (!activeRole) {
      return <UnifiedLoader message={t('loading.creatingProfile')} fullScreen />;
    }
  }

  return (
    <div className="min-h-screen bg-practitioner-auth animate-fade-in-up">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 pt-20 pb-4">
        <div className="w-full max-w-md">
          <div className="text-left mb-8">
            <h1 className="text-2xl font-semibold text-amber-800">
              {t('practitionerPortal')}
            </h1>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-neutral-100 p-1">
            <button
              onClick={() => setActiveTab('login')}
              className={cn(
                "py-2.5 text-center text-sm font-medium rounded-md transition-all",
                activeTab === 'login' 
                  ? "bg-white text-amber-700 shadow-sm" 
                  : "text-neutral-600 hover:text-neutral-800"
              )}
            >
              {t('login')}
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={cn(
                "py-2.5 text-center text-sm font-medium rounded-md transition-all",
                activeTab === 'register' 
                  ? "bg-white text-amber-700 shadow-sm" 
                  : "text-neutral-600 hover:text-neutral-800"
              )}
            >
              {t('register')}
            </button>
          </div>

          <div className="rounded-xl border border-amber-200/70 bg-white/95 backdrop-blur p-6 shadow-sm">
            <h2 className="mb-6 text-xl font-semibold text-amber-800">
              {activeTab === 'login' ? t('welcomeBack') : t('createPractitionerAccount')}
            </h2>

            {activeTab === 'login' ? (
              <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">{t('email')}</label>
                  <div className="relative">
                    <Input
                      {...loginForm.register("email")}
                      placeholder={t('emailPlaceholder')}
                      type="email"
                      className="border-neutral-300 bg-neutral-50 pl-10"
                      disabled={loading}
                    />
                    <span className="absolute left-3 top-2.5 text-neutral-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </span>
                  </div>
                  {loginForm.formState.errors.email && (
                    <p className="text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">{t('password')}</label>
                  <div className="relative">
                    <Input
                      {...loginForm.register("password")}
                      type={showPassword ? "text" : "password"}
                      placeholder={t('passwordPlaceholder')}
                      className="border-neutral-300 bg-neutral-50 pl-10"
                      disabled={loading}
                    />
                    <span className="absolute left-3 top-2.5 text-neutral-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-2.5 text-neutral-500"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-sm text-amber-600 hover:text-amber-800 hover:underline">
                    {t('forgotPassword')}
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="mt-2 w-full bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('loggingIn')}
                    </>
                  ) : (
                    t('loginButton')
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-4">
                {/* Referral Code Banner */}
                {(referralCode || referralRemoved) && (
                  <div className="relative">
                    <Alert className={referralRemoved ? "border-neutral-200 bg-neutral-50" : "border-amber-200 bg-amber-50"}>
                      <Gift className={referralRemoved ? "h-4 w-4 text-neutral-500" : "h-4 w-4 text-amber-600"} />
                      <AlertTitle className={referralRemoved ? "text-neutral-700 pr-8" : "text-amber-900 pr-8"}>
                        {referralRemoved ? tSettings('referral.referralRemoved') : tSettings('referral.usingReferralCode')}
                      </AlertTitle>
                      <AlertDescription className={referralRemoved ? "text-neutral-600" : "text-amber-800"}>
                        {referralRemoved ? (
                          tSettings('referral.referralRemovedDesc')
                        ) : (
                          <>
                            {tSettings('referral.codeWillBeApplied')} <span className="font-mono font-bold">{referralCode}</span>
                          </>
                        )}
                      </AlertDescription>
                    </Alert>
                    {!referralRemoved && (
                      <button
                        type="button"
                        onClick={() => {
                          setReferralCode(null);
                          setReferralRemoved(true);
                        }}
                        className="absolute top-3 right-3 text-amber-600 hover:text-amber-800 transition-colors z-10"
                        aria-label={tSettings('referral.removeReferral')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">{t('email')}</label>
                  <div className="relative">
                    <Input
                      {...registerForm.register("email")}
                      placeholder={t('emailPlaceholder')}
                      type="email"
                      className="border-neutral-300 bg-neutral-50 pl-10"
                      disabled={loading}
                    />
                    <span className="absolute left-3 top-2.5 text-neutral-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </span>
                  </div>
                  {registerForm.formState.errors.email && (
                    <p className="text-xs text-red-500">{registerForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">{t('firstName')}</label>
                    <div className="relative">
                      <Input
                        {...registerForm.register("firstName")}
                        placeholder={t('firstNamePlaceholder')}
                        type="text"
                        className="border-neutral-300 bg-neutral-50 pl-10"
                        disabled={loading}
                      />
                      <span className="absolute left-3 top-2.5 text-neutral-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                    </div>
                    {registerForm.formState.errors.firstName && (
                      <p className="text-xs text-red-500">{registerForm.formState.errors.firstName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">{t('lastName')}</label>
                    <div className="relative">
                      <Input
                        {...registerForm.register("lastName")}
                        placeholder={t('lastNamePlaceholder')}
                        type="text"
                        className="border-neutral-300 bg-neutral-50 pl-10"
                        disabled={loading}
                      />
                      <span className="absolute left-3 top-2.5 text-neutral-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                    </div>
                    {registerForm.formState.errors.lastName && (
                      <p className="text-xs text-red-500">{registerForm.formState.errors.lastName.message}</p>
                    )}
                  </div>
                </div>


                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">{t('password')}</label>
                  <div className="relative">
                    <Input
                      {...registerForm.register("password")}
                      type={showPassword ? "text" : "password"}
                      placeholder={t('passwordPlaceholder')}
                      className="border-neutral-300 bg-neutral-50 pl-10"
                      disabled={loading}
                    />
                    <span className="absolute left-3 top-2.5 text-neutral-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-2.5 text-neutral-500"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {registerForm.formState.errors.password && (
                    <p className="text-xs text-red-500">{registerForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">{t('confirmPassword')}</label>
                  <div className="relative">
                    <Input
                      {...registerForm.register("confirmPassword")}
                      type={showPassword ? "text" : "password"}
                      placeholder={t('passwordPlaceholder')}
                      className="border-neutral-300 bg-neutral-50 pl-10"
                      disabled={loading}
                    />
                    <span className="absolute left-3 top-2.5 text-neutral-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-2.5 text-neutral-500"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {registerForm.formState.errors.confirmPassword && (
                    <p className="text-xs text-red-500">{registerForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="mt-2 w-full bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('registering')}
                    </>
                  ) : (
                    t('registerButton')
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-neutral-500">{t('orContinueWith')}</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <Button 
                  variant="outline" 
                  className="border-neutral-300 bg-white text-neutral-700"
                  onClick={signInWithGoogle}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('google')}
                </Button>
                <Button 
                  variant="outline" 
                  className="border-neutral-300 bg-white text-neutral-700"
                  onClick={signInWithLinkedIn}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  {t('linkedIn')}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-neutral-600">
            {t('byContinuing')}
          </div>

          <div className="mt-8 text-center">
            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
              <p className="text-neutral-600">{t('areClient')}</p>
              <Link 
                to="/client-auth" 
                className="mt-2 inline-block text-amber-700 hover:text-amber-800 hover:underline"
              >
                {t('gotoClientPortal')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerAuth;
